import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styles from './ViewMoreBetsModal.module.css';
import { API_BASE_URL } from "../../config/api";

export default function ViewMoreBetsModal({ onClose, matchId, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'Matched');
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [ip, setIp] = useState('');
  const [amountFrom, setAmountFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  const token = localStorage.getItem("ownerToken") || localStorage.getItem("staffToken") || localStorage.getItem("token");

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      if (!token || !matchId) return;
      setLoading(true);
      try {
        let data = [];
        if (activeTab === 'Matched') {
           const res = await axios.get(`${API_BASE_URL}/user/cricket/matched-bets/all?eventid=${matchId}`, {
             headers: { Authorization: `Bearer ${token}` }
           });
           // API likely returns data in res.data.data
           data = res.data?.data || [];
        } else if (activeTab === 'Unmatched') {
            // As per requirement, Unmatched tab should not show data (or be disabled)
            data = [];
        } else {
           // Deleted/Deleted Bets - Placeholder
           data = [];
        }
        setBets(data);
      } catch (err) {
        console.error("Error fetching bets:", err);
        setBets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, matchId, token]);

  const handleReset = () => {
    setSearchUser('');
    setIp('');
    setAmountFrom('');
    setAmountTo('');
    setTypeFilter('All');
  };

  // Filter Data
  const filteredBets = useMemo(() => {
    return bets.filter(bet => {
      // Username filter
      const username = bet.username || bet.user_name || "Self"; // Adjust field based on API
      if (searchUser && !username.toLowerCase().includes(searchUser.toLowerCase())) return false;

      // IP filter
      const betIp = bet.ip || bet.ip_address || "";
      if (ip && !betIp.includes(ip)) return false;

      // Amount filter
      const amount = parseFloat(bet.stake || bet.amount || bet.stake_amount || 0);
      if (amountFrom && amount < parseFloat(amountFrom)) return false;
      if (amountTo && amount > parseFloat(amountTo)) return false;

      // Type Filter
      const bType = (bet.type || bet.bet_type || (bet.is_back ? 'BACK' : 'LAY')).toUpperCase();
      if (typeFilter !== 'All' && bType !== typeFilter.toUpperCase()) return false;

      return true;
    });
  }, [bets, searchUser, ip, amountFrom, amountTo, typeFilter]);

  // Export CSV
  const handleDownloadCSV = () => {
    if (filteredBets.length === 0) return;
    
    const headers = ["Username", "Market Name", "Nation", "Type", "Rate", "Amount", "Date", "IP"];
    const csvContent = [
      headers.join(","),
      ...filteredBets.map(bet => {
        return [
          bet.username || bet.user_name || "Self",
          bet.game_type || bet.marketName || "match_odds",
          bet.selection_name || bet.team_name,
          bet.type || bet.bet_type || (bet.is_back ? 'BACK' : 'LAY'),
          bet.odds || bet.user_rate,
          bet.stake || bet.amount || bet.stake_amount,
          bet.created_at || bet.placed_date,
          bet.ip || bet.ip_address
        ].join(",");
      })
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bets_${matchId}_${activeTab}.csv`;
    a.click();
  };

  return (
    <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1" role="dialog">
        <div className={`modal-dialog ${styles.modalDialogCustom}`} role="document">
            <div className={`modal-content ${styles.casinoResultModal}`}>
                <header className={`modal-header ${styles.modalHeader}`}>
                    <h5 className={`modal-title ${styles.modalTitle}`}>
                        View More Bets
                    </h5>
                    <button
                        type="button"
                        className={`close ${styles.closeButton}`}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </header>
                <div className={`modal-body ${styles.modalBody}`}>
                    <div className={styles.modalContent}>
                        
                        {/* Tabs */}
                        <div className={styles.navTitle}>
                            <button 
                                className={`${styles.matchedBetTab} ${activeTab === 'Matched' ? styles.active : ''}`}
                                onClick={() => setActiveTab('Matched')}
                            >
                                Matched
                            </button>
                            <button 
                                className={`${styles.matchedBetTab} ${activeTab === 'Unmatched' ? styles.active : ''}`}
                                onClick={() => setActiveTab('Unmatched')}
                            >
                                Unmatched
                            </button>
                            <button 
                                className={`${styles.matchedBetTab} ${activeTab === 'Deleted' ? styles.active : ''}`}
                                onClick={() => setActiveTab('Deleted')}
                            >
                                Deleted
                            </button>
                            <button className={styles.downloadBtn} onClick={handleDownloadCSV}>Download CSV</button>
                        </div>

                        <div className={styles.filterSection}>
                             <div className={styles.filterRow}>
                                <div className={styles.filterGroup}>
                                  <label className={styles.filterLabel}>Search by user</label>
                                  <input 
                                    className={styles.filterInput} 
                                    placeholder="Enter Atleast 3 character" 
                                    value={searchUser}
                                    onChange={e => setSearchUser(e.target.value)}
                                  />
                                </div>
                                <div className={styles.filterGroup}>
                                  <label className={styles.filterLabel}>IP Address</label>
                                  <input 
                                    className={styles.filterInput} 
                                    placeholder="IP Address" 
                                    value={ip}
                                    onChange={e => setIp(e.target.value)}
                                  />
                                </div>
                                <div className={styles.filterGroup}>
                                  <label className={styles.filterLabel}>Amount</label>
                                  <div className={styles.amountGroup}>
                                     <input 
                                        className={styles.filterInput} 
                                        placeholder="From" 
                                        type="number"
                                        value={amountFrom}
                                        onChange={e => setAmountFrom(e.target.value)}
                                     />
                                     <span className={styles.amountSeparator}>-</span>
                                     <input 
                                        className={styles.filterInput} 
                                        placeholder="To" 
                                        type="number"
                                        value={amountTo}
                                        onChange={e => setAmountTo(e.target.value)}
                                     />
                                  </div>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label className={styles.filterLabel}>Type</label>
                                  <select className={styles.filterSelect} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                                    <option value="All">All</option>
                                    <option value="BACK">Back</option>
                                    <option value="LAY">Lay</option>
                                  </select>
                                </div>
                             </div>
                             
                             <div className={styles.filterActions}>
                                <button className={styles.searchBtn}>Search</button>
                                <button className={styles.resetBtn} onClick={handleReset}>Reset</button>
                             </div>
                        </div>

                        <div className={styles.tableContainer}>
                            {loading ? <div className={styles.loading}>Loading...</div> : (
                                <table className={styles.couponTable}>
                                    <thead className={styles.tableHeader}>
                                        <tr>
                                        <th>No</th>
                                        <th>Username</th>
                                        <th>Market Name</th>
                                        <th>Nation</th>
                                        <th>Type</th>
                                        <th>User Rate</th>
                                        <th>Amount</th>
                                        <th>Currency</th>
                                        <th>Place Date</th>
                                        <th>Match Date</th>
                                        <th>IP</th>
                                        <th>Browser Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredBets.length > 0 ? filteredBets.map((bet, idx) => {
                                           const type = bet.type || bet.bet_type || (bet.is_back ? 'BACK' : 'LAY'); // Default to BACK/LAY logic if simple type not there
                                           const isLay = type.toUpperCase() === 'LAY';
                                           
                                           // Determine classes based on bet type for coloring rows
                                           // If Matched/Unmatched typically have different coloring needs, adjust here. 
                                           // Usually Back = Blue, Lay = Pink for odds columns, but here row execution.
                                           const rowClass = isLay ? styles.layRow : styles.backRow;

                                           return (
                                            <tr key={idx} className={rowClass}>
                                                <td>{idx + 1}</td>
                                                <td>{bet.username || bet.user_name || "Self"}</td>
                                                <td>{bet.game_type || bet.marketName || "match_odds"}</td>
                                                <td>{bet.selection_name || bet.team_name}</td>
                                                <td>{type}</td>
                                                <td>{bet.odds || bet.user_rate}</td>
                                                <td>{bet.stake || bet.amount || bet.stake_amount}</td>
                                                <td>INR</td>
                                                <td>{bet.created_at || bet.placed_date || "-"}</td>
                                                <td>{bet.match_date || "-"}</td>
                                                <td>{bet.ip || bet.ip_address || "-"}</td>
                                                <td>Detail</td>
                                            </tr>
                                           );
                                        }) : (
                                            <tr><td colSpan="12" className={styles.noRecords}>No records found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
}
